import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import React, { useCallback, memo } from 'react';

import { CollapsibleList } from 'ui-components/components/collapsible-list';

import * as OrdersDrawerActions from '../../store/orders/drawer/orders.drawer.actions';
import { emptyArray } from 'ui-components/default-props/empty';
import * as UserActions from '../../store/user/user.actions';
import Global from '../../store/global-selectors';
import { MultiOrderHeader } from '../../containers/orders-drawer/sections/multi-order-header';
import OrdersGridDrawer from '../../components/orders-drawer';
import SingleOrderHeader from '../../components/orders-drawer/sections/single-order-header';
import { OrdersDrawerService } from '../../services/orders-drawer-service';
import { getIsServiceLocalDelivery } from '../../hooks/use-is-service-local-delivery';
import { PICKUP_INFORMATION } from '../../constants/csw-ids';
import { EntitiesAdapterForFulfillmentPlannerContext } from '../../fulfillment-planner';
import { FulfillmentPlanService } from '../../services/fulfillment-plan-service';
import { SIDEBAR } from '../../constants/component-names';
import { SingleFulfillmentPlanOrdersGridSidebarDrawer } from '../../components/orders-drawer/single-fulfillment-plan-orders-grid-sidebar-drawer';
import { useEnableSingleFulfillmentPlanOrdersGridSidebarDrawerRedesign } from '../../feature-flags/hooks/use-enable-single-fulfillment-plan-orders-grid-sidebar-drawer-redesign';

import { DroppableDrawerModules } from './components/droppable-drawer-modules';

import style from './components/orders-drawer-components.module.less';

const getMultiOrderSections = (salesOrderIds, fulfillmentPlanIds, modules) => (
  <React.Fragment>
    <MultiOrderHeader />
    <CollapsibleList className={style['orders-drawer-scrollable']}>
      <DroppableDrawerModules fulfillmentPlanIds={fulfillmentPlanIds} modules={modules} salesOrderIds={salesOrderIds} />
    </CollapsibleList>
  </React.Fragment>
);

const getDrawerSectionsSelector = createSelector(
  ({ salesOrderIds = emptyArray }) => JSON.stringify(salesOrderIds),
  ({ selectedIds = emptyArray }) => selectedIds,
  ({ tagIds = emptyArray }) => tagIds,
  ({ singleOrderDrawerModules = emptyArray }) => singleOrderDrawerModules,
  ({ multiOrderDrawerModules = emptyArray }) => multiOrderDrawerModules,
  (_, fulfillmentPlanIds) => fulfillmentPlanIds,
  ({ areAllSelected = false }) => areAllSelected,
  (_, __, orderGridStatusesJSON) => orderGridStatusesJSON,
  (_, __, ___, isSingleFulfillmentPlanSidebarRedesignEnabled) => isSingleFulfillmentPlanSidebarRedesignEnabled,
  (
    salesOrderIdsJSON,
    selectedIds,
    tagIds,
    singleOrderDrawerModules,
    multiOrderDrawerModules,
    fulfillmentPlanIds,
    areAllSelected,
    orderGridStatusesJSON,
    isSingleFulfillmentPlanSidebarRedesignEnabled,
  ) => {
    const salesOrderIds = JSON.parse(salesOrderIdsJSON);
    const uniqueFulfillmentPlanIds = [...new Set(fulfillmentPlanIds)];
    if (selectedIds.length === 0 && !areAllSelected) {
      return null;
    }

    if (selectedIds.length === 1) {
      const singleOrderModules = OrdersDrawerService.getSingleFulfillmentPlanOrderedComponents(
        singleOrderDrawerModules,
        isSingleFulfillmentPlanSidebarRedesignEnabled,
      );
      const [fulfillmentPlanId] = selectedIds;
      const orderGridStatuses = JSON.parse(orderGridStatusesJSON);
      const isReadOnly = !FulfillmentPlanService.shouldShowShipmentWidget({
        orderGridStatus: orderGridStatuses[0],
      });

      if (isSingleFulfillmentPlanSidebarRedesignEnabled) {
        return (
          <SingleFulfillmentPlanOrdersGridSidebarDrawer
            fulfillmentPlanId={fulfillmentPlanId}
            isReadOnly={isReadOnly}
            modules={singleOrderModules}
            salesOrderIds={salesOrderIds}
            tagIds={tagIds}
          />
        );
      }

      return (
        <EntitiesAdapterForFulfillmentPlannerContext
          key={fulfillmentPlanId}
          fulfillmentPlanId={fulfillmentPlanId}
          isReadOnly={isReadOnly}
          parentComponentName={SIDEBAR}
        >
          <SingleOrderHeader />
          <CollapsibleList className={style['orders-drawer-scrollable']}>
            <DroppableDrawerModules
              currentFulfillmentPlanId={fulfillmentPlanId}
              fulfillmentPlanIds={uniqueFulfillmentPlanIds}
              modules={singleOrderModules}
              salesOrderIds={salesOrderIds}
              tagIds={tagIds}
            />
          </CollapsibleList>
        </EntitiesAdapterForFulfillmentPlannerContext>
      );
    }

    const multiOrdersModules = !areAllSelected
      ? OrdersDrawerService.getMultiSelectionOrderedModules(multiOrderDrawerModules)
      : OrdersDrawerService.getAllSelectedOrderedModules();
    return getMultiOrderSections(salesOrderIds, uniqueFulfillmentPlanIds, multiOrdersModules);
  },
);

const getEntitiesAreLoadingForOrders = (state, salesOrderIds = emptyArray) => {
  return salesOrderIds.reduce((acc, salesOrderId) => {
    if (acc || !salesOrderId) {
      return acc;
    }
    const isSalesOrderLoading = Global.selectEntitiesSalesOrderIsLoading(state, { salesOrderId });
    const salesOrder = Global.selectEntitiesSalesOrderById(state, {
      salesOrderId,
    });

    return acc || (!salesOrder && isSalesOrderLoading);
  }, false);
};

const getSingleOrderDrawerModules = (state, fulfillmentPlanIds) => {
  const defaultModules = Global.selectOrdersDrawerSingleOrderDrawerModulesList(state) || emptyArray;
  const fulfillmentPlanId = fulfillmentPlanIds.length === 1 && fulfillmentPlanIds[0];
  const singleServiceId =
    Global.selectEntitiesFulfillmentPlanServiceId(state, {
      fulfillmentPlanId,
    }) || null;
  const isFulfillmentPlanLocalDelivery = singleServiceId && getIsServiceLocalDelivery(singleServiceId);
  if (!isFulfillmentPlanLocalDelivery) {
    return defaultModules;
  }
  const isPickupInformationInModules = !!defaultModules?.find((mod) => mod === PICKUP_INFORMATION);
  const [defaultFirstModule, ...restDefaultModules] = defaultModules ?? [];
  return isPickupInformationInModules ? defaultModules : [defaultFirstModule, PICKUP_INFORMATION, ...restDefaultModules];
};

export const OrdersGridDrawerContainer = memo(() => {
  const dispatch = useDispatch();

  const reorderMultiSelectionDrawerModules = useCallback(
    (newOrderOfModules) => {
      dispatch(OrdersDrawerActions.reorderMultiSelectionDrawerModules(newOrderOfModules));
    },
    [dispatch],
  );
  const reorderSingleSelectionDrawerModules = useCallback(
    (newOrderOfModules) => {
      dispatch(OrdersDrawerActions.reorderSingleSelectionDrawerModules(newOrderOfModules));
    },
    [dispatch],
  );
  const saveUserSetting = useCallback(
    (setting) => {
      dispatch(UserActions.saveUserSetting(setting));
    },
    [dispatch],
  );

  const fulfillmentPlanIds = useSelector(Global.selectSelectedOrdersIds);
  const orderGridStatusesJSON = useSelector((state) => {
    return JSON.stringify(
      fulfillmentPlanIds.map((fulfillmentPlanId) =>
        Global.selectEntitiesFulfillmentPlanOrderGridStatus(state, {
          fulfillmentPlanId,
        }),
      ),
    );
  });
  const salesOrderIds = useSelector((state) =>
    Global.selectEntitiesSalesOrderIdsByFulfillmentPlanIds(state, {
      fulfillmentPlanIds,
    }),
  );

  const loadingState = useSelector(Global.selectOrdersDrawerLoadingState);
  const entitiesLoading = useSelector((state) => getEntitiesAreLoadingForOrders(state, salesOrderIds));
  const isLoading = loadingState || entitiesLoading;
  const multiOrderDrawerModules = useSelector(Global.selectOrdersDrawerMultiOrderDrawerModulesList) || emptyArray;
  const areAllOrdersSelected = useSelector(Global.selectSelectedOrdersAreAllOrdersSelected);
  const tagIds = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId: fulfillmentPlanIds[0],
      property: 'tagIds',
    }),
  );

  const singleOrderDrawerModules = useSelector((state) => getSingleOrderDrawerModules(state, fulfillmentPlanIds));
  const isSingleFulfillmentPlanSidebarRedesignEnabled = useEnableSingleFulfillmentPlanOrdersGridSidebarDrawerRedesign();

  const drawerSections = getDrawerSectionsSelector(
    {
      salesOrderIds,
      selectedIds: fulfillmentPlanIds,
      tagIds,
      singleOrderDrawerModules,
      multiOrderDrawerModules,
      areAllSelected: areAllOrdersSelected,
    },
    fulfillmentPlanIds,
    orderGridStatusesJSON,
    isSingleFulfillmentPlanSidebarRedesignEnabled,
  );

  const _onModuleReorder = useCallback(
    (reorderAction) => {
      const isMulti = fulfillmentPlanIds.length > 1;
      const newOrderOfModules = OrdersDrawerService.changeOrderInArray(
        isMulti ? multiOrderDrawerModules : singleOrderDrawerModules,
        reorderAction,
      );
      const moduleOrderSetting = {
        settingName: isMulti ? OrdersDrawerService.getV3MultiModuleSettingName() : OrdersDrawerService.getV3SingleModuleSettingName(),
        settingValue: newOrderOfModules,
        excludeV3prefix: true,
      };

      saveUserSetting(moduleOrderSetting);

      return isMulti ? reorderMultiSelectionDrawerModules(newOrderOfModules) : reorderSingleSelectionDrawerModules(newOrderOfModules);
    },
    [
      fulfillmentPlanIds.length,
      multiOrderDrawerModules,
      reorderMultiSelectionDrawerModules,
      reorderSingleSelectionDrawerModules,
      saveUserSetting,
      singleOrderDrawerModules,
    ],
  );

  return (
    <OrdersGridDrawer
      areAllOrdersSelected={areAllOrdersSelected}
      drawerSections={drawerSections}
      fulfillmentPlanIds={fulfillmentPlanIds}
      isLoading={isLoading}
      onModuleReorder={_onModuleReorder}
    />
  );
});
